import { motion } from 'framer-motion';
import expectation from '../../assets/logo png/weexpect.jpg';
import { FaRocket, FaBrain, FaUsers, FaSmile, FaArrowRight } from 'react-icons/fa';

const decorativeSvg = "https://framerusercontent.com/images/vPkLrm9UJiyijCDtcCPWb74bhoI.svg";

const WhatWeExpect = () => {
  const expectations = [
    {
      icon: <FaRocket className="text-4xl text-red-500 group-hover:text-red-600 transition-colors" />,
      title: 'Skills',
      description: 'Mastery in modern tools, frameworks, and best practices.',
    },
    {
      icon: <FaBrain className="text-4xl text-red-500 group-hover:text-red-600 transition-colors" />,
      title: 'Learning Attitude',
      description: 'Always curious, constantly growing, never settling.',
    },
    {
      icon: <FaUsers className="text-4xl text-red-500 group-hover:text-red-600 transition-colors" />,
      title: 'Team Work',
      description: 'Collaborative spirit, shared goals, collective success.',
    },
    {
      icon: <FaSmile className="text-4xl text-red-500 group-hover:text-red-600 transition-colors" />,
      title: 'Positive Thinking',
      description: 'Solution‑oriented mindset that turns challenges into opportunities.',
    },
  ];

  return (
    <section className="relative bg-gradient-to-br from-gray-50 via-white to-gray-100 py-20 md:py-28 overflow-hidden">
      {/* Animated blobs (existing) */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-rose-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      {/* Left decorative SVG – smaller, moves from top to bottom */}
      <div className="absolute left-0 top-10 w-48 md:w-56 opacity-50 pointer-events-none z-0">
        <div className="relative w-full h-full">
          <img
            src={decorativeSvg}
            alt=""
            className="w-full h-auto animate-float-left drop-shadow-lg"
            style={{ transform: 'scaleX(-1)' }}
            onError={(e) => {
              // Fallback: show a red gradient circle instead of broken image
              e.target.onerror = null;
              e.target.style.display = 'none';
              // Create a fallback element
              const parent = e.target.parentElement;
              if (parent && !parent.querySelector('.fallback')) {
                const fallback = document.createElement('div');
                fallback.className = 'fallback w-full h-full rounded-full bg-gradient-to-br from-red-400/40 to-rose-400/40 blur-md';
                parent.appendChild(fallback);
              }
            }}
          />
        </div>
      </div>

      {/* Right decorative SVG – smaller, moves from bottom to top */}
      <div className="absolute right-0 bottom-10 w-48 md:w-56 opacity-50 pointer-events-none z-0">
        <div className="relative w-full h-full">
          <img
            src={decorativeSvg}
            alt=""
            className="w-full h-auto animate-float-right drop-shadow-lg"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              const parent = e.target.parentElement;
              if (parent && !parent.querySelector('.fallback')) {
                const fallback = document.createElement('div');
                fallback.className = 'fallback w-full h-full rounded-full bg-gradient-to-br from-red-400/40 to-rose-400/40 blur-md';
                parent.appendChild(fallback);
              }
            }}
          />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-extrabold">
            <span className="text-red-600">What</span>{' '}
            <span className="text-gray-900">We Expect?</span>
          </h2>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto text-lg">
            These are the values that define our culture and drive our excellence.
          </p>
        </motion.div>

        {/* Main content: left cards + right image */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side: expectation cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {expectations.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                className="group relative bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-white/50"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">{item.icon}</div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-1 group-hover:text-red-600 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
                <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <FaArrowRight className="text-red-400 text-sm" />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Right side: clean image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            viewport={{ once: true }}
            className="relative group"
          >
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src={expectation}
                alt="Team collaboration"
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          </motion.div>
        </div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }

        /* Left SVG: moves from top to bottom */
        @keyframes floatLeft {
          0% { transform: translateY(0) scaleX(-1); }
          50% { transform: translateY(40px) scaleX(-1); }
          100% { transform: translateY(0) scaleX(-1); }
        }
        .animate-float-left {
          animation: floatLeft 6s ease-in-out infinite;
        }

        /* Right SVG: moves from bottom to top */
        @keyframes floatRight {
          0% { transform: translateY(0); }
          50% { transform: translateY(-40px); }
          100% { transform: translateY(0); }
        }
        .animate-float-right {
          animation: floatRight 6s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default WhatWeExpect;